import { Action, createAction, props } from '@ngrx/store';
import {
  ApiError,
  CloudflareMeta,
  Error,
  MailingListPerson,
} from '@models/app.models';
import { TokenContext } from '@models/responses/token-response.model';
import { HttpRequest } from '@angular/common/http';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { AutoSearch } from '@models/auto-search.model';
import {DynamicPageConfig} from "@models/cms/dynamic-pages-umbraco/dynamic-pages-config.model";

export const enum AppActionTypes {
  abandonToken = '[app] ABANDON_TOKEN',
  abandonTokenSuccess = '[app] ABANDON_TOKEN_SUCCESS',
  apiClearErrors = '[app] API_CLEAR_ERRORS',
  apiError = '[app] API_ERROR',
  appAddNotification = '[app] ADD_NOTIFICATION',
  appBeginCancellation = '[app] BEGIN_CANCELLATION',
  appClearNotifications = '[app] CLEAR_NOTIFICATIONS',
  appGetShowModifySearchWidget = '[app] GET_SHOW_MODIFY_SEARCH_WIDGET',
  appMailingListAddPerson = '[app] MAILING_LIST_ADD_PERSON',
  appMailingListAddPersonSuccess = '[app] MAILING_LIST_ADD_PERSON_SUCCESS',
  appRemoveNotification = '[app] REMOVE_NOTIFICATION',
  appSetAutoSearch = '[app] SET_AUTO_SEARCH',
  showTokenModalBeforeTimeout = '[app] SHOW_TOKEN_MODAL_BEFORE_TIMEOUT',
  showTokenModal = '[app] SHOW_TOKEN_MODAL_AFTER_TIMEOUT',
  refreshTokenFromTimeoutModal = '[app] REFRESH_TOKEN',
  refreshTokenFromTimeoutModalSuccess = '[app] REFRESH_TOKEN_SUCCESS',
  start = '[app] START',
  createTokenSuccess = '[app] CREATE_TOKEN_SUCCESS',
  norseApiRequest = '[app] NORSE_API_REQUEST',
  norseApiRequestCompleted = '[app] NORSE_API_REQUEST_COMPLETED',
  appTokenExpired = '[app] TOKEN_EXPIRED',
  getTokenInfoFailure = '[app] GET_TOKEN_INFO_FAILURE',
  getTokenInfoSuccess = '[app] GET_TOKEN_INFO_SUCCESS',
  showLoader = '[app] SHOW_LOADER',
  getTokenKeepAlive = '[app] GET_TOKEN_KEEPALIVE',
  handleGtmScript = '[app] HANDLE_GTM_SCRIPT',
  hideLoader = '[app] HIDE_LOADER',
  noOp = '[app] NO_OPERATION',
  appSetShowModifySearchWidget = '[app] SET_SHOW_MODIFY_SEARCH_WIDGET',
  appUpdateModifySearchWidget = '[app] UPDATE_MODIFY_SEARCH_WIDGET',
  setLoading = '[app] SET_LOADING',
  setActiveStep = '[app] SET_ACTIVE_STEP',
  resetSession = '[app] RESET_SESSION',
  resetApp = '[App] RESET_APP',
  resetSteps = '[app] RESET_STEPS',
  appSetNotifications = '[app] SET_NOTIFICATIONS',
  showCookieModal = '[app] SHOW_COOKIE_MODAL',
  getTokenkeepAliveSuccess = '[app] GET_TOKEN_KEEPALIVE_SUCCESS',
  formError = '[app] FORM_ERROR',
  clearFormError = '[app] CLEAR_FORM_ERROR',
  error = '[app] ERROR',
  appStoreWindowSize = '[app] STORE_WINDOW_SIZE',
  userCountryGeolocated = '[app] USER_COUNTRY_GEOLOCATED',
  userGeolocationFailed = '[app] USER_GEOLOCATION_FAILED',
  appUserLocated = '[app] USER_LOCATED',
  appUserLocateFailed = '[app] USER_LOCATE_FAILED',
  showTripSummary = '[app] SHOW_TRIP_SUMMARY',
  hideTripSummary = '[app] HIDE_TRIP_SUMMARY',
  toggleTripSummary = '[app] TOGGLE_TRIP_SUMMARY',
  autoRefreshToken = '[app] TOKEN_AUTOREFRESH',
  updateCloudflareMeta = '[app] UPDATE_CLOUDFLARE_META',
  loadDynamicPagesConfig = '[app] LOAD_DYNAMIC_PAGES_CONFIG',
  loadDynamicPagesConfigSuccess = '[app] LOAD_DYNAMIC_PAGES_CONFIG_SUCCESS',
}

export const userCountryGeolocated = createAction(
  AppActionTypes.userCountryGeolocated,
  props<{
    countryCode: string | null;
  }>(),
);

export const userGeolocationFailed = createAction(
  AppActionTypes.userGeolocationFailed,
);

export const appUserLocated = createAction(
  AppActionTypes.appUserLocated,
  props<{
    lat: number;
    lon: number;
    byPrompt: boolean;
  }>(),
);

export const appUserLocateFailed = createAction(
  AppActionTypes.appUserLocateFailed,
);

export const appStoreWindowSize = createAction(
  AppActionTypes.appStoreWindowSize,
  props<{
    height: number;
    width: number;
    isMobileFromTablet?: boolean;
  }>(),
);

export const appBeginCancellation = createAction(
  AppActionTypes.appBeginCancellation,
);

export const appMailingListAddPerson = createAction(
  AppActionTypes.appMailingListAddPerson,
  props<{
    person: MailingListPerson;
  }>(),
);

export const appMailingListAddPersonSuccess = createAction(
  AppActionTypes.appMailingListAddPersonSuccess,
  props<{
    person: MailingListPerson;
  }>(),
);

export const appShowCookieModal = createAction(AppActionTypes.showCookieModal);

export const appHandleGtmScript = createAction(AppActionTypes.handleGtmScript);

export const appStart = createAction(
  AppActionTypes.start,
  props<{
    platforms: string[];
    width: number;
    height: number;
    isMobileFromTablet?: boolean;
  }>(),
);

export const appCreateTokenSuccess = createAction(
  AppActionTypes.createTokenSuccess,
  props<{ idleTimeoutInMinutes: number }>(),
);

export const appResetSteps = createAction(AppActionTypes.resetSteps);

export const appSetActiveStep = createAction(
  AppActionTypes.setLoading,
  props<{ stepName: FlowPathEnum }>(),
);

export const appSetLoading = createAction(
  AppActionTypes.setLoading,
  props<{ loading: boolean }>(),
);

export const appReset = createAction(
  AppActionTypes.resetApp,
  props<{ width: number, height: number, isMobileFromTablet?: boolean }>()
);

export const appResetSession = createAction(
  AppActionTypes.resetSession,
  props<{
    excludedFlows: string[];
    next: Action | Action[];
    resetBookingOnly?: boolean;
  }>(),
);

export const onError = createAction(
  AppActionTypes.error,
  props<{ error: Error }>(),
);

export const appApiError = createAction(
  AppActionTypes.apiError,
  props<{ errors: ApiError[] }>(),
);

export const appApiClearErrors = createAction(AppActionTypes.apiClearErrors);

export const onFormError = createAction(
  AppActionTypes.formError,
  props<{ error: Error }>(),
);

export const appClearFormError = createAction(AppActionTypes.clearFormError);

export const showTokenModalBeforeTimeout = createAction(
  AppActionTypes.showTokenModalBeforeTimeout,
);

export const showTokenModal = createAction(AppActionTypes.showTokenModal);

export const appRefreshTokenFromTimeoutModal = createAction(
  AppActionTypes.refreshTokenFromTimeoutModal,
);

export const appRefreshTokenFromTimeoutModalSuccess = createAction(
  AppActionTypes.refreshTokenFromTimeoutModalSuccess,
);

export const appNorseApiRequest = createAction(
  AppActionTypes.norseApiRequest,
  props<{ req: HttpRequest<unknown> }>(),
);

export const appNorseApiRequestCompleted = createAction(
  AppActionTypes.norseApiRequestCompleted,
  props<{ req: HttpRequest<unknown> }>(),
);

export const appShowLoader = createAction(AppActionTypes.showLoader);

export const appHideLoader = createAction(AppActionTypes.hideLoader);

export const appAbandonToken = createAction(AppActionTypes.abandonToken);

export const appAbandonTokenSuccess = createAction(
  AppActionTypes.abandonTokenSuccess,
);

export const appSetAutoSearch = createAction(
  AppActionTypes.appSetAutoSearch,
  props<{ payload: AutoSearch }>(),
);

export const appGetTokenInfoSuccess = createAction(
  AppActionTypes.getTokenInfoSuccess,
  props<{ info: TokenContext; next?: Action }>(),
);

export const appTokenExpired = createAction(AppActionTypes.appTokenExpired);

export const appGetTokenInfoFailure = createAction(
  AppActionTypes.getTokenInfoFailure,
);

export const appGetTokenKeepAlive = createAction(
  AppActionTypes.getTokenKeepAlive,
);

export const appGetTokenKeepAliveSuccess = createAction(
  AppActionTypes.getTokenkeepAliveSuccess,
);

export const appNoOp = createAction(AppActionTypes.noOp);

export const appSetShowModifySearchWidget = createAction(
  AppActionTypes.appSetShowModifySearchWidget,
  props<{ show: boolean }>(),
);

export const appUpdateModifySearchWidget = createAction(
  AppActionTypes.appUpdateModifySearchWidget,
  props<{
    showWidget: boolean;
    showLink: boolean;
  }>(),
);

export const appShowTripSummary = createAction(AppActionTypes.showTripSummary);
export const appHideTripSummary = createAction(AppActionTypes.hideTripSummary);
export const appToggleTripSummary = createAction(
  AppActionTypes.toggleTripSummary,
);

export const autoRefreshToken = createAction(AppActionTypes.autoRefreshToken);

export const updateCloudflareMeta = createAction(
  AppActionTypes.updateCloudflareMeta,
  props<{
    meta: CloudflareMeta | null;
  }>()
);

export const loadDynamicPagesConfig = createAction(
  AppActionTypes.loadDynamicPagesConfig,
);

export const loadDynamicPagesConfigSuccess = createAction(
  AppActionTypes.loadDynamicPagesConfigSuccess,
  props<{
    config: DynamicPageConfig[] | null;
  }>()
);
